import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useMsal } from '@azure/msal-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { loginRequest } from './authConfig';
import CountUp from './components/CountUp';

const api = axios.create({ baseURL: '' });

// --- Status Colors ---
const STATUS_COLORS = {
    'Open': { bg: 'bg-blue-500/20', text: 'text-blue-400', border: 'border-blue-500/30', dot: 'bg-blue-400' },
    'Pending': { bg: 'bg-amber-500/20', text: 'text-amber-400', border: 'border-amber-500/30', dot: 'bg-amber-400' },
    'Resolved': { bg: 'bg-emerald-500/20', text: 'text-emerald-400', border: 'border-emerald-500/30', dot: 'bg-emerald-400' },
    'Closed': { bg: 'bg-gray-500/20', text: 'text-gray-400', border: 'border-gray-500/30', dot: 'bg-gray-500' },
    'default': { bg: 'bg-purple-500/20', text: 'text-purple-400', border: 'border-purple-500/30', dot: 'bg-purple-400' },
};

const getStatusColor = (status) => STATUS_COLORS[status] || STATUS_COLORS['default'];

const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
        const date = new Date(dateString);
        const now = new Date();
        const diffMs = now - date;
        const diffHours = diffMs / (1000 * 60 * 60);

        if (diffHours < 1) return `${Math.floor(diffMs / 60000)}m ago`;
        if (diffHours < 24) return `${Math.floor(diffHours)}h ago`;
        if (diffHours < 168) return `${Math.floor(diffHours / 24)}d ago`;
        return new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric' }).format(date);
    } catch { return dateString; }
};

const launchpadGlassCardStyle = {
    backgroundColor: 'rgba(255, 255, 255, 0.03)',
    backdropFilter: 'blur(64px) saturate(200%)',
    WebkitBackdropFilter: 'blur(64px) saturate(200%)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.16), inset 0 -1px 0 rgba(255, 255, 255, 0.04), 0 30px 86px rgba(0, 0, 0, 0.44)',
};

const supportGlassCardStyles = `
    .support-launchpad-card:hover,
    .support-launchpad-card:focus-visible {
        border-color: rgba(255, 255, 255, 0.18) !important;
        background-color: rgba(255, 255, 255, 0.045) !important;
    }
`;

const StatCard = ({ label, value }) => {
    return (
        <div className="support-launchpad-card relative group overflow-hidden rounded-2xl p-5 transition-all duration-300 hover:-translate-y-1" style={launchpadGlassCardStyle}>
            <div className="relative z-10 flex flex-col h-full justify-between items-center text-center">
                <div className="flex justify-center items-center w-full mb-2">
                    <span className="text-xs font-semibold text-gray-500 uppercase tracking-widest">{label}</span>
                </div>

                <div className="flex items-baseline justify-center gap-2 mt-1">
                    <span className="text-3xl text-white tracking-tighter" style={{
                        fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "Segoe UI", Roboto, sans-serif',
                        fontWeight: 700,
                        fontFeatureSettings: '"tnum"',
                        textShadow: '0 1px 0px rgba(255, 255, 255, 0.2)'
                    }}>
                        <CountUp end={value} />
                    </span>
                </div>
            </div>
        </div>
    );
};


// --- Ticket Detail Panel (Slide-Over) ---
export const TicketDetailPanel = ({ ticketId, onClose, getAuthToken }) => {
    const [ticket, setTicket] = useState(null);
    const [comments, setComments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [replyText, setReplyText] = useState('');
    const [sending, setSending] = useState(false);
    const [isVisible, setIsVisible] = useState(false);
    const chatEndRef = useRef(null);

    useEffect(() => { setTimeout(() => setIsVisible(true), 50); }, []);

    const fetchDetail = useCallback(async () => {
        try {
            const token = await getAuthToken();
            const res = await api.get(`/api/atera/tickets/${ticketId}`, { headers: { 'x-auth-token': token } });
            setTicket(res.data.ticket);
            setComments(res.data.comments);
        } catch (err) { console.error(err); }
        finally { setLoading(false); }
    }, [ticketId, getAuthToken]);

    useEffect(() => { fetchDetail(); }, [fetchDetail]);
    useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [comments]);

    const handleSendReply = async () => {
        if (!replyText.trim()) return;
        setSending(true);
        try {
            const token = await getAuthToken();
            await api.post(`/api/atera/tickets/${ticketId}/reply`, { message: replyText }, { headers: { 'x-auth-token': token } });
            setReplyText('');
            fetchDetail(); // Refresh comments
        } catch (err) { console.error('Reply failed:', err); }
        finally { setSending(false); }
    };

    const handleClose = () => {
        setIsVisible(false);
        setTimeout(onClose, 300);
    };

    const statusTheme = ticket ? getStatusColor(ticket.status) : getStatusColor('default');

    return (
        <div className={`fixed inset-0 z-50 flex justify-end transition-opacity duration-300 ${isVisible ? 'opacity-100' : 'opacity-0'}`}>
            {/* Backdrop */}
            <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={handleClose}></div>

            {/* Panel */}
            <div className={`relative w-full max-w-lg bg-[#0a0a0a]/80 backdrop-blur-2xl border-l border-white/10 shadow-2xl flex flex-col transform transition-transform duration-300 ease-out ${isVisible ? 'translate-x-0' : 'translate-x-full'}`}>
                {/* Header */}
                <div className="shrink-0 h-20 flex items-center justify-between px-6 border-b border-white/5 bg-white/[0.02]">
                    <div className="flex-1 min-w-0">
                        {ticket && (
                            <>
                                <div className="flex items-center gap-2 mb-1">
                                    <span className={`text-xs font-mono px-2 py-0.5 rounded-full border ${statusTheme.bg} ${statusTheme.text} ${statusTheme.border}`}>{ticket.status}</span>
                                    <span className="text-xs text-gray-500 font-mono">#{ticket.number}</span>
                                </div>
                                <h2 className="text-sm font-semibold text-white truncate">{ticket.title}</h2>
                            </>
                        )}
                    </div>
                    <button onClick={handleClose} className="text-gray-500 hover:text-white transition-colors p-2">
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                    </button>
                </div>

                {/* Conversation */}
                <style>{`
                    .atera-comment, .atera-comment * {
                        color: #e2e8f0 !important;
                        background-color: transparent !important;
                        background: transparent !important;
                        border-color: rgba(255,255,255,0.1) !important;
                        font-family: 'Inter', sans-serif !important;
                    }
                    .atera-comment a { color: #60a5fa !important; text-decoration: underline !important; }
                    .atera-comment img { max-width: 120px !important; height: auto !important; border-radius: 8px !important; opacity: 0.9; }
                    .atera-comment table { border-collapse: collapse !important; }
                    .atera-comment td, .atera-comment th { padding: 2px 4px !important; border: none !important; }
                    .atera-comment b, .atera-comment strong { color: #f1f5f9 !important; font-weight: 600 !important; }
                    .atera-comment p { margin: 4px 0 !important; }
                    .atera-comment br + br { display: none; }
                    .atera-comment font { color: #e2e8f0 !important; }
                    .atera-comment span { color: #e2e8f0 !important; }
                `}</style>
                <div className="flex-1 overflow-y-auto custom-scrollbar p-6 space-y-4">
                    {loading ? (
                        <div className="flex items-center justify-center h-full">
                            <div className="w-6 h-6 border-2 border-white/20 border-t-white rounded-full animate-spin"></div>
                        </div>
                    ) : comments.length === 0 ? (
                        <div className="text-center text-gray-500 text-sm italic py-10">No conversation history yet.</div>
                    ) : (
                        comments.filter(c => !c.isInternal).map((comment, idx) => (
                            <div key={comment.id || idx} className={`flex ${comment.isEndUser ? 'justify-end' : 'justify-start'}`}>
                                <div className={`max-w-[85%] rounded-2xl px-4 py-3 ${comment.isEndUser
                                    ? 'bg-blue-600/20 border border-blue-500/20 rounded-br-md'
                                    : 'bg-white/[0.07] border border-white/10 rounded-bl-md'
                                    }`}>
                                    <div className="flex items-center gap-2 mb-2">
                                        <span className={`text-xs font-semibold ${comment.isEndUser ? 'text-blue-400' : 'text-emerald-400'}`}>
                                            {comment.creatorName}
                                        </span>
                                        <span className="text-xs text-gray-500">{formatDate(comment.date)}</span>
                                    </div>
                                    <div className="atera-comment text-sm leading-relaxed whitespace-pre-wrap break-words">
                                        {comment.text || 'No comment text.'}
                                    </div>
                                </div>
                            </div>
                        ))
                    )}
                    <div ref={chatEndRef} />
                </div>

                {/* Reply Box */}
                <div className="shrink-0 border-t border-white/5 p-4 bg-white/[0.02]">
                    <div className="flex gap-3 items-end">
                        <textarea
                            value={replyText}
                            onChange={(e) => setReplyText(e.target.value)}
                            placeholder="Type your reply..."
                            rows={2}
                            className="flex-1 bg-[#0a0a0a]/50 border border-white/10 text-white rounded-xl px-4 py-3 focus:ring-1 focus:ring-blue-500/30 focus:outline-none placeholder-gray-600 text-sm resize-none custom-scrollbar"
                            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSendReply(); } }}
                        />
                        <button
                            onClick={handleSendReply}
                            disabled={!replyText.trim() || sending}
                            className="h-11 w-11 shrink-0 rounded-xl bg-blue-600 hover:bg-blue-500 disabled:bg-gray-700 disabled:text-gray-500 text-white flex items-center justify-center transition-all duration-200 shadow-lg shadow-blue-900/30"
                        >
                            {sending ? (
                                <div className="w-4 h-4 border-2 border-white/20 border-t-white rounded-full animate-spin"></div>
                            ) : (
                                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" /></svg>
                            )}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

// --- New Ticket Modal ---
const NewTicketModal = ({ onClose, onSubmit, initialTitle = '', initialDescription = '', titlePrefix = 'New Support Ticket' }) => {
    const [title, setTitle] = useState(initialTitle);
    const [description, setDescription] = useState(initialDescription);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => { setTimeout(() => setIsVisible(true), 50); }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!title.trim()) return;
        setIsSubmitting(true);
        await onSubmit({ title, description });
        setIsSubmitting(false);
        handleClose();
    };

    const handleClose = () => { setIsVisible(false); setTimeout(onClose, 300); };

    return (
        <div className={`fixed inset-0 z-50 flex items-center justify-center p-4 transition-opacity duration-300 ${isVisible ? 'opacity-100' : 'opacity-0'}`}>
            <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={handleClose} />
            <div className={`relative w-full max-w-md bg-[#0a0a0a]/90 backdrop-blur-2xl border border-white/10 rounded-2xl shadow-2xl p-6 transform transition-all duration-300 ${isVisible ? 'scale-100' : 'scale-95'}`}>
                <h2 className="text-lg font-bold text-white mb-4">{titlePrefix}</h2>
                <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                        <label className="text-xs font-mono text-gray-500 uppercase tracking-wider mb-1 block">Subject</label>
                        <input type="text" value={title} onChange={(e) => setTitle(e.target.value)} className="w-full bg-[#0a0a0a]/50 border border-white/10 text-white rounded-xl px-4 py-3 focus:ring-1 focus:ring-blue-500/30 focus:outline-none placeholder-gray-600 text-sm" placeholder="Brief description of the issue..." required />
                    </div>
                    <div>
                        <label className="text-xs font-mono text-gray-500 uppercase tracking-wider mb-1 block">Details</label>
                        <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={4} className="w-full bg-[#0a0a0a]/50 border border-white/10 text-white rounded-xl px-4 py-3 focus:ring-1 focus:ring-blue-500/30 focus:outline-none placeholder-gray-600 text-sm resize-none" placeholder="Provide any additional context..." />
                    </div>
                    <div className="flex justify-end gap-3 pt-2">
                        <button type="button" onClick={handleClose} className="px-4 py-2 text-sm text-gray-400 hover:text-white transition-colors">Cancel</button>
                        <button type="submit" disabled={isSubmitting || !title.trim()} className="px-6 py-2 text-sm font-bold text-white bg-blue-600 rounded-xl hover:bg-blue-500 transition-colors shadow-lg shadow-blue-900/20 disabled:opacity-50">
                            {isSubmitting ? 'Submitting...' : 'Submit Ticket'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};


// =================================================================
// --- MAIN SUPPORT PAGE ---
// =================================================================
export default function SupportPage({ isPreviewMode = false }) {
    const { instance, inProgress } = useMsal();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [loading, setLoading] = useState(true);
    const [tickets, setTickets] = useState([]);
    const [locationScope, setLocationScope] = useState({ status: 'default', location: null, ticketSummary: null });
    const [error, setError] = useState('');
    const [selectedTicketId, setSelectedTicketId] = useState(null);
    const [isNewTicketOpen, setIsNewTicketOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [statusFilter, setStatusFilter] = useState('Open'); // Default to Open
    const locationId = searchParams.get('locationId');
    const parsedLocationId = locationId ? Number(locationId) : null;
    const scopedLocationId = Number.isInteger(parsedLocationId) && parsedLocationId > 0 ? parsedLocationId : null;

    const getAuthToken = useCallback(async () => {
        const accounts = instance.getAllAccounts();
        if (!accounts[0]) throw new Error("No accounts");
        const res = await instance.acquireTokenSilent({ ...loginRequest, account: accounts[0] });
        return res.accessToken;
    }, [instance]);

    useEffect(() => {
        if (locationId && !scopedLocationId) {
            navigate('/support', { replace: true });
        }
    }, [locationId, navigate, scopedLocationId]);

    const fetchTickets = useCallback(async () => {
        if (inProgress !== "none") return;
        setLoading(true); setError('');
        try {
            const token = await getAuthToken();
            if (scopedLocationId) {
                setLocationScope({ status: 'loading', location: null, ticketSummary: null });
                const res = await api.get(`/api/locations/profiles/${scopedLocationId}/support-summary`, { headers: { 'x-auth-token': token } });
                setTickets(res.data.ticketSummary?.tickets || []);
                setLocationScope({
                    status: 'ready',
                    location: res.data.location || null,
                    ticketSummary: res.data.ticketSummary || null
                });
            } else {
                setLocationScope({ status: 'default', location: null, ticketSummary: null });
                const res = await api.get('/api/atera/tickets', { headers: { 'x-auth-token': token } });
                setTickets(res.data.tickets || []);
            }
        } catch (err) {
            setLocationScope(scopedLocationId ? { status: 'error', location: null, ticketSummary: null } : { status: 'default', location: null, ticketSummary: null });
            const isAuthorizationError = err.response && (err.response.status === 401 || err.response.status === 403);
            if (isPreviewMode && isAuthorizationError) {
                setTickets([]);
            } else {
                console.error(err);
                setError(err.response?.status === 403 ? 'Access Denied' : 'Failed to load tickets.');
            }
        } finally { setLoading(false); }
    }, [getAuthToken, inProgress, scopedLocationId, isPreviewMode]);

    useEffect(() => {
        if (isPreviewMode) {
            setLoading(false);
            setError('');
            return;
        }

        fetchTickets();
    }, [fetchTickets, isPreviewMode]);

    const handleCreateTicket = async (formData) => {
        try {
            const token = await getAuthToken();
            await api.post('/api/atera/tickets', scopedLocationId ? { ...formData, locationId: scopedLocationId } : formData, { headers: { 'x-auth-token': token } });
            fetchTickets();
        } catch (err) { console.error(err); }
    };

    const handleClearLocationScope = () => {
        setSearchQuery('');
        setStatusFilter('Open');
        setSelectedTicketId(null);
        navigate('/support');
    };

    // --- Filtering ---
    // Remove "Deleted" tickets from the main dataset entirely so they don't show up in filters or counts
    const activeTickets = tickets.filter(t => t.status !== 'Deleted');
    const isLocationScoped = Boolean(scopedLocationId);
    const visibleTickets = activeTickets;

    const filteredTickets = visibleTickets.filter(t => {
        const matchesSearch = !searchQuery ||
            t.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
            (t.customerName || '').toLowerCase().includes(searchQuery.toLowerCase());
        const matchesStatus = statusFilter === 'all' || t.status === statusFilter;
        return matchesSearch && matchesStatus;
    });

    const openCount = visibleTickets.filter(t => t.status === 'Open').length;
    const pendingCount = visibleTickets.filter(t => t.status === 'Pending').length;
    const resolvedCount = visibleTickets.filter(t => t.status === 'Resolved' || t.status === 'Closed').length;

    const statusOptions = ['all', ...new Set(visibleTickets.map(t => t.status))];
    const scopedLocationName = locationScope.location?.name || locationScope.location?.boardName || 'Selected Location';
    const scopedLocationAddress = locationScope.location?.address || '';
    const newTicketInitialDescription = isLocationScoped && locationScope.location
        ? `Location: ${scopedLocationName}\nLocation ID: ${locationScope.location.locationId || scopedLocationId}\nAddress: ${scopedLocationAddress || 'Not available'}\n\nIssue details:\n`
        : '';

    if (loading) return <div className="h-full flex items-center justify-center text-sm text-gray-500">Loading tickets...</div>;
    if (error) return <div className="h-full flex items-center justify-center text-red-400">{error}</div>;

    return (
        <div className="flex flex-col h-full animate-fadeInUp px-6 py-6 overflow-hidden relative z-10">
            <style>{supportGlassCardStyles}</style>
            {/* Header */}
            <div className="flex justify-between items-center mb-6 shrink-0 gap-4">
                {isLocationScoped ? (
                    <div className="rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-3">
                        <p className="text-sm font-semibold text-white">
                            {locationScope.status === 'ready' ? `Support for ${scopedLocationName}` : 'Support for selected location'}
                        </p>
                        <button
                            type="button"
                            onClick={handleClearLocationScope}
                            className="mt-1 text-xs font-semibold text-blue-300 transition-colors hover:text-white"
                        >
                            All Support Tickets
                        </button>
                    </div>
                ) : (
                    <div />
                )}
                <div className="flex items-center gap-4">
                    {/* Search */}
                    <div className="relative group">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <svg className="h-4 w-4 text-gray-500 group-focus-within:text-white transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
                        </div>
                        <input type="text" placeholder="Search tickets..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="text-white rounded-xl pl-10 pr-4 py-2 w-64 focus:w-80 transition-all duration-300 focus:outline-none placeholder-gray-500 text-sm" style={launchpadGlassCardStyle}
                        onFocus={(e) => {
                            e.target.style.borderColor = 'rgba(255, 255, 255, 0.18)';
                            e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.045)';
                        }}
                        onBlur={(e) => {
                            e.target.style.borderColor = 'rgba(255, 255, 255, 0.1)';
                            e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.03)';
                        }}
                        />
                    </div>
                    {/* New Ticket Button */}
                    <button onClick={() => setIsNewTicketOpen(true)} className="flex items-center gap-2 text-white px-4 py-2 rounded-xl text-sm transition-all duration-300" style={{
                        background: 'rgba(255, 255, 255, 0.1)',
                        border: '1px solid rgba(255, 255, 255, 0.2)',
                        backdropFilter: 'blur(20px)',
                        WebkitBackdropFilter: 'blur(20px)',
                        fontWeight: 600,
                        fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "Segoe UI", Roboto, sans-serif'
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)'; e.currentTarget.style.boxShadow = '0 0 15px rgba(255,255,255,0.1)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)'; e.currentTarget.style.boxShadow = 'none'; }}
                    >
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
                        {isLocationScoped ? 'New Ticket for This Location' : 'New Ticket'}
                    </button>
                </div>
            </div>

            {/* Stats Row */}
            <div className="grid grid-cols-4 gap-4 mb-6 shrink-0">
                <StatCard label="Total Tickets" value={visibleTickets.length} color="blue" />
                <StatCard label="Open" value={openCount} color="amber" />
                <StatCard label="Pending" value={pendingCount} color="red" />
                <StatCard label="Resolved" value={resolvedCount} color="emerald" />
            </div>

            {/* Status Filter Pills */}
            <div className="flex gap-2 mb-4 shrink-0">
                {statusOptions.map(status => (
                    <button key={status} onClick={() => setStatusFilter(status)} className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all duration-200 ${statusFilter === status ? 'bg-white/10 text-white border border-white/20' : 'bg-transparent text-gray-500 border border-white/5 hover:text-white hover:bg-white/5'}`}>
                        {status === 'all' ? 'All' : status}
                    </button>
                ))}
            </div>

            {/* Ticket Table */}
            <div className="flex-1 overflow-y-auto min-h-0 custom-scrollbar">
                {filteredTickets.length === 0 ? (
                    <div className="support-launchpad-card flex items-center justify-center p-16 rounded-2xl relative overflow-hidden" style={launchpadGlassCardStyle}>
                        <style>{`
                            @keyframes terminalPulse {
                                0% { opacity: 0.3; }
                                50% { opacity: 0.6; }
                                100% { opacity: 0.3; }
                            }
                            .animate-terminal-pulse {
                                animation: terminalPulse 4s ease-in-out infinite;
                            }
                        `}</style>
                        <div className="relative z-10 animate-terminal-pulse" style={{
                            color: 'rgba(255, 255, 255, 0.4)',
                            textTransform: 'uppercase',
                            letterSpacing: '0.15em',
                            fontSize: '12px',
                            fontWeight: 500
                        }}>
                            {visibleTickets.length === 0 ? 'No tickets found for this view.' : 'No tickets match your filter.'}
                        </div>
                    </div>
                ) : (
                    <div className="space-y-2">
                        {filteredTickets.map((ticket, idx) => {
                            const statusTheme = getStatusColor(ticket.status);
                            return (
                                <div
                                    key={ticket.id}
                                    onClick={() => setSelectedTicketId(ticket.id)}
                                    className="support-launchpad-card group relative overflow-hidden rounded-2xl transition-all duration-300 hover:-translate-y-0.5 cursor-pointer animate-fadeInUp"
                                    style={{ ...launchpadGlassCardStyle, animationDelay: `${idx * 50}ms` }}
                                >
                                    {/* Left accent bar */}
                                    <div className={`absolute left-0 top-0 bottom-0 w-[3px] ${statusTheme.dot} opacity-60 group-hover:opacity-100 transition-opacity duration-300`} />

                                    <div className="relative z-10 flex items-center px-6 py-4 gap-8">
                                        {/* Status badge — fixed width for alignment */}
                                        <div className="shrink-0 w-24">
                                            <span className={`inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full border ${statusTheme.bg} ${statusTheme.text} ${statusTheme.border}`}>
                                                <span className={`w-1.5 h-1.5 rounded-full ${statusTheme.dot}`}></span>
                                                {ticket.status}
                                            </span>
                                        </div>

                                        {/* Subject — left-aligned */}
                                        <div className="shrink-0 min-w-0 max-w-[50%]">
                                            <p className="text-sm font-semibold text-white group-hover:text-blue-100 transition-colors truncate">{ticket.title}</p>
                                            <p className="text-xs text-gray-500 mt-0.5">#{ticket.number} • {ticket.customerName}</p>
                                        </div>

                                        {/* Animated timeline connector */}
                                        <div className="flex-1 flex items-center px-3 relative overflow-hidden">
                                            {/* Base dashed line */}
                                            <div className="w-full border-b border-dashed border-white/[0.06] group-hover:border-white/[0.12] transition-colors duration-500" />
                                            {/* Traveling light pulse — visible on hover */}
                                            <div
                                                className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"
                                                style={{
                                                    background: 'linear-gradient(90deg, transparent 0%, transparent 30%, rgba(59,130,246,0.25) 50%, transparent 70%, transparent 100%)',
                                                    backgroundSize: '200% 100%',
                                                    animation: 'none',
                                                    maskImage: 'linear-gradient(to bottom, transparent 40%, black 50%, transparent 60%)',
                                                    WebkitMaskImage: 'linear-gradient(to bottom, transparent 40%, black 50%, transparent 60%)',
                                                }}
                                            />
                                            <style>{`
                                                .group:hover .timeline-pulse {
                                                    animation: timeline-sweep 5s ease-in-out infinite;
                                                }
                                                @keyframes timeline-sweep {
                                                    0% { background-position: 200% 0; }
                                                    100% { background-position: -200% 0; }
                                                }
                                            `}</style>
                                            {/* Actual animated pulse element */}
                                            <div
                                                className="timeline-pulse absolute inset-0 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                                                style={{
                                                    background: 'linear-gradient(90deg, transparent 0%, transparent 35%, rgba(59,130,246,0.3) 50%, transparent 65%, transparent 100%)',
                                                    backgroundSize: '200% 100%',
                                                    maskImage: 'linear-gradient(to bottom, transparent 35%, black 50%, transparent 65%)',
                                                    WebkitMaskImage: 'linear-gradient(to bottom, transparent 35%, black 50%, transparent 65%)',
                                                }}
                                            />
                                        </div>

                                        {/* Updated */}
                                        <div className="shrink-0 text-right">
                                            <p className="text-xs text-gray-500 font-mono">{formatDate(ticket.lastModified)}</p>
                                        </div>

                                        {/* Arrow */}
                                        <div className="shrink-0">
                                            <svg className="w-4 h-4 text-gray-600 group-hover:text-white group-hover:translate-x-0.5 transition-all duration-200" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* Overlays */}
            {selectedTicketId && <TicketDetailPanel ticketId={selectedTicketId} onClose={() => setSelectedTicketId(null)} getAuthToken={getAuthToken} />}
            {isNewTicketOpen && (
                <NewTicketModal
                    onClose={() => setIsNewTicketOpen(false)}
                    onSubmit={handleCreateTicket}
                    initialTitle=""
                    initialDescription={newTicketInitialDescription}
                    titlePrefix={isLocationScoped ? 'New Ticket for This Location' : 'New Support Ticket'}
                />
            )}
        </div>
    );
}
